import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import ReactCrop from "react-image-crop";
import { setColor, setFont } from "../../../../../assets/styles/styles";
import { putBannerImage, putPersonalizationItem } from "../../../../../api/RoomsController";
import { uploadFile, checkUrlImageSize } from "../../../../../api/FilesController";
import Banner from "../../../../../components/layout/ui/Banner";
import Button from "../../../../../components/common/button/Button";
import Typography from "../../../../../components/common/text/Typography";

import "react-image-crop/dist/ReactCrop.css";
import DeleteIcon from "../../../../../assets/images/icons/Delete.svg";
import Iconify from "../../../../../components/common/icon/Iconify";

const FILE = "file";
const VIDEO = "video";
const CROP = "crop";
const LINK = "link";

const MediaUploader = ({ frame, typeUpload, setIsOpen, setSuccessBannerMessage, token, userId, imageUrl }) => {
    const placeHolderText =
        frame.frameType === VIDEO
            ? "https://player.vimeo.com/progressive_redirect/playback/1008935729/rendition/720p/file.mp4?loc=external&signature=a48..."
            : "https://www.myimage.com/cats.jpg";
    const [placeholder, setPlaceholder] = useState(placeHolderText);
    const [maxWidth, maxHeight] =
        frame.frameType === "thumbnail" ? ["490", "274"] : frame.recommendedFileSize.split("x"); /// preset size for images for thumbnail or based on image type
    const aspect = maxWidth / maxHeight;
    const imageRef = useRef(null);
    const [mode, setMode] = useState(typeUpload || FILE);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [crop, setCrop] = useState({
        unit: "%",
        x: 0,
        y: 0,
        height: 15,
        maxHeight,
        maxWidth,
        aspect,
    });
    const [src, setSrc] = useState(null);
    const [fileName, setFileName] = useState(null);
    const [file, setFile] = useState(null);
    const [croppedImageUrl, setCroppedImageUrl] = useState(null);
    const [openBanner, setOpenBanner] = useState("");
    const [bannerMessage, setBannerMessage] = useState("");
    const [bannerType, setBannerType] = useState("ERROR");

    const [optionalName, setOptionalName] = useState("");

    const runBanner = (message) => {
        setBannerMessage(message);
        setBannerType("ERROR");
        setOpenBanner(true);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (file.size > 5242880) {
            runBanner("File size must be less than 5 MB.");
            setIsLoading(false);
            return;
        }

        if (!file.type.startsWith("image/")) {
            runBanner("Please select an image file.");
            setIsLoading(false);
            return;
        }

        URL.createObjectURL(file);
        setFile(file);
        setFileName(file.name);

        const reader = new FileReader();
        reader.onload = () => {
            const imageSrc = reader.result;
            setSrc(imageSrc);

            if (frame.displayName === "Square Image Frame") {
                const img = new Image();
                img.onload = () => {
                    let width = img.width;
                    let height = img.height;

                    const maxDimension = 500;
                    if (width < height && width > maxDimension) {
                        height = (height * maxDimension) / width;
                        width = maxDimension;
                    } else if (height < width && height > maxDimension) {
                        width = (width * maxDimension) / height;
                        height = maxDimension;
                    }

                    const canvas = document.createElement("canvas");
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob((blob) => {
                        if (!blob) {
                            runBanner("Error resizing the image.");
                            return;
                        }

                        const resizedFile = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });

                        setFile(resizedFile);
                        setFileName(resizedFile.name);

                        const resizedImageUrl = URL.createObjectURL(resizedFile);
                        setSrc(resizedImageUrl);

                        setMode(CROP);
                    }, file.type);
                };

                img.onerror = () => {
                    runBanner("Error loading the image.");
                };

                img.src = imageSrc;
            } else {
                setMode(CROP);
            }
        };
        reader.onerror = (error) => {};

        reader.readAsDataURL(file);
    };

    const isValidUrl = async (url) => {
        const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9.-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?\.(jpg|jpeg|png)$/i;
        if (!urlRegex.test(url)) {
            return false;
        }

        return new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = url;
        });
    };

    const isValidImageFile = (filename) => {
        const fileRegex = /\.(jpg|png|jpeg)$/i;
        return fileRegex.test(filename);
    };

    const isVideoUrl = (url) => {
        const videoRegex = /^(https?:\/\/)?(www\.)?(vimeo\.com\/\d+|player\.vimeo\.com\/.*\.mp4)(\?.*)?$/;
        return videoRegex.test(url);
    };

    const isFileSizeLessThan5MB = (fileSizeInBytes) => {
        const sizeLimitInBytes = 5 * 1024 * 1024; // 5 MB in bytes
        return fileSizeInBytes <= sizeLimitInBytes;
    };

    const onImageLoaded = (image) => {
        imageRef.current = image;
    };

    const onCropComplete = (crop) => {
        makeClientCrop(crop);
    };

    const onCropChange = (crop) => {
        setCrop(crop);
    };

    const makeClientCrop = async (crop) => {
        if (imageRef.current && crop.width && crop.height) {
            const croppedImageFile = await getCroppedImg(imageRef.current, crop, "croppedImage.png");
            setCroppedImageUrl(croppedImageFile);
        }
    };

    const handleSaveImage = () => {
        setMode(FILE);
        setFile(croppedImageUrl);
    };

    const returnToFile = () => {
        setMode(FILE);
        clearAllOptions();
    };

    const clearAllOptions = () => {
        setFile(null);
        setCroppedImageUrl(null);
        setFileName(null);
        setSrc(null);

        setInputValue("");
    };

    const getCroppedImg = (image, crop, fileName) => {
        const canvas = document.createElement("canvas");
        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;
        canvas.width = crop.width;
        canvas.height = crop.height;
        const ctx = canvas.getContext("2d");

        ctx.drawImage(
            image,
            crop.x * scaleX,
            crop.y * scaleY,
            crop.width * scaleX,
            crop.height * scaleY,
            0,
            0,
            crop.width,
            crop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (!blob) {
                    console.error("Canvas is empty");
                    return;
                }
                const newFile = new File([blob], fileName, {
                    type: "image/png",
                    lastModified: Date.now(),
                });

                resolve(newFile);
            }, "image/png");
        });
    };

    const handleSubmitMainThumbnail = async () => {
        setIsLoading(true);

        if (mode === LINK) {
            const validUrl = await isValidUrl(inputValue);

            if (!validUrl) {
                runBanner("Invalid Url, should contain jpg | jpeg | png extension.");
                setIsLoading(false);
                return;
            }

            const checkUrlSize = await checkUrlImageSize(inputValue);

            if (!checkUrlSize?.data?.valid) {
                runBanner("Invalid Url image size, maximum file size of 5 MB");
                setIsLoading(false);
                return;
            }

            const response = await putBannerImage(
                frame.roomId,
                { bannerImageKey: null, bannerImageExternalUrl: inputValue },
                token
            );

            if (response.status === 200) {
                setSuccessBannerMessage("URL updated");
                setIsOpen(false);
            } else {
                runBanner("Invalid URL provided");
                setIsLoading(false);
            }
        }

        if (mode === FILE) {
            file.usageType = "roomBannerImage";
            if (!isValidImageFile(file.name)) {
                runBanner("There was an error uploading your file. Please use .jpg or .png files");
                setIsLoading(false);
                return;
            }

            if (!isFileSizeLessThan5MB(file.size)) {
                runBanner("Media exceeds maximum file size of 5 MB");
                setIsLoading(false);
                return;
            }

            try {
                const fileUploadedResponse = await uploadFile(
                    { file, usageType: "roomBannerImage", roomId: frame.roomId, targetUserId: userId },
                    token
                );

                const response = await putBannerImage(
                    frame.roomId,
                    { bannerImageKey: fileUploadedResponse.data.id, bannerImageExternalUrl: null },
                    token
                );

                if (response.status === 200) {
                    setSuccessBannerMessage("Changes are live in Intraverse!");
                    setIsOpen(false);
                } else {
                    runBanner("There was an error with uploading your file.");
                }
            } catch (error) {
                console.log(error);
                runBanner("There was an error with uploading your file.");
            } finally {
                setIsLoading(false);
            }
        }
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        if (frame.isMain) {
            await handleSubmitMainThumbnail();
        }

        if (mode === LINK) {
            if (frame.frameType === VIDEO) {
                const validVideoUrl = await isVideoUrl(inputValue);

                if (!validVideoUrl) {
                    runBanner("Invalid URL provided. Please provide a Vimeo link");
                    setIsLoading(false);
                    return;
                }
            } else {
                const validUrl = await isValidUrl(inputValue);

                if (!validUrl) {
                    runBanner("Invalid Url, should contain jpg | jpeg | png extension.");
                    setIsLoading(false);
                    return;
                }

                const checkUrlSize = await checkUrlImageSize(inputValue);

                if (!checkUrlSize.data.valid) {
                    runBanner("Invalid Url image size, maximum file size of 5 MB");
                    setIsLoading(false);
                    return;
                }
            }

            const response = await putPersonalizationItem(
                frame.id,
                {
                    isExternalUrl: true,
                    externalUrl: inputValue,
                    fileId: null,
                },
                token
            );

            if (response.status === 204 || response.status === 200) {
                setSuccessBannerMessage("Successfully updated URL");
                setIsOpen(false);
            } else {
                runBanner("Invalid URL Provided");
                setIsLoading(false);
            }
        }

        if (mode === FILE) {
            file.usageType = "roomDetailsImage";

            if (!isValidImageFile(file.name)) {
                runBanner("There was an error uploading your file. Please use .jpg, .jpeg or .png files");
                setIsLoading(false);
                return;
            }

            if (!isFileSizeLessThan5MB(file.size)) {
                runBanner("Media exceeds maximum file size of 5 MB");
                setIsLoading(false);
                return;
            }

            let fileToUpload = file;
            if (optionalName && optionalName.trim().length > 0) {
                const extension = file.name.substring(file.name.lastIndexOf("."));
                const newFileName = optionalName.trim() + extension;
                fileToUpload = new File([file], newFileName, {
                    type: file.type,
                    lastModified: file.lastModified,
                });
            }

            let response = await uploadFile(
                { file: fileToUpload, usageType: "roomDetailsImage", roomId: frame.roomId, targetUserId: userId },
                token,
                optionalName
            );

            if (response.status === 200) {
                const fileId = response.data.id;
                response = await putPersonalizationItem(
                    frame.id,
                    {
                        isExternalUrl: false,
                        externalUrl: null,
                        fileId,
                    },
                    token
                );

                if (response.status === 204 || response.status === 200) {
                    setSuccessBannerMessage("Changes are live in Intraverse!");
                    setIsOpen(false);
                }
            } else {
                runBanner("Error Uploading File");
            }

            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (openBanner) {
            const timer = setTimeout(() => {
                setOpenBanner(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [openBanner]);

    const fadeContainerRefs = useRef([]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.remove("fade");
                    } else {
                        entry.target.classList.add("fade");
                    }
                });
            },
            {
                root: document.querySelector(".modal-content"),
                threshold: 0.1,
            }
        );

        const currentRefs = fadeContainerRefs.current;
        currentRefs.forEach((ref) => {
            if (ref) observer.observe(ref);
        });

        return () => {
            currentRefs.forEach((ref) => {
                if (ref) observer.unobserve(ref);
            });
        };
    }, []);

    return (
        <MediaUploaderContainer>
            <Banner
                message={bannerMessage}
                type={bannerType}
                openBanner={openBanner}
                setOpenBanner={setOpenBanner}
                topDisplay
                left={"0"}
                showCloseButton={false}
                position={"absolute"}
            />

            {mode === CROP ? (
                <>
                    <Typography variant="h3Custom" color="#FBFCFF" align="center" fontSize="24px" FontWeight="700">
                        Crop {frame.frameType === VIDEO ? "Video" : "Image"}
                    </Typography>
                    <CropContainer>
                        <ReactCrop
                            src={src}
                            crop={crop}
                            ruleOfThirds
                            onImageLoaded={onImageLoaded}
                            onComplete={onCropComplete}
                            onChange={onCropChange}
                            maxWidth={maxWidth}
                            maxHeight={maxHeight}
                            keepSelection
                            aspect={aspect}
                            minWidth={25}
                            minHeight={25}
                        />
                    </CropContainer>
                    <Button
                        variant="PurpleButton"
                        onClick={() => handleSaveImage()}
                        label={"Save and Crop"}
                        margin={"15px 0"}
                        style={{ width: "100%" }}
                        onHoverColor="white"
                    />

                    <Typography
                        style={{ cursor: "pointer" }}
                        variant="pWhite"
                        FontWeight={"700"}
                        letterSpacing={"0px"}
                        fontSize={"16px"}
                        onClick={() => {
                            clearAllOptions();
                            setIsOpen(false);
                        }}
                        margin={"20px 0 0 0"}
                    >
                        Go Back
                    </Typography>
                </>
            ) : (
                <>
                    <Typography variant="h3Custom" color="#FBFCFF" align="center" fontSize="24px" FontWeight="700">
                        Upload {frame.frameType === VIDEO ? "Video" : "Image"}
                    </Typography>
                    <SmallSubtitle>
                        Customize by uploading a {frame.frameType === VIDEO ? "video " : "image "} for{" "}
                        <span>{frame.displayName}</span>.
                    </SmallSubtitle>

                    {imageUrl !== null && imageUrl !== undefined && (
                        <ImageContainer>
                            <ImageFrame>
                                <PreviewImage src={imageUrl} alt="Preview" />

                                <Typography
                                    style={{ textTransform: "capitalize" }}
                                    variant="h3Custom"
                                    color={setColor.circularProgress}
                                    fontSize="18px"
                                    FontWeight="500"
                                    margin={"5px 0px 10px 0px"}
                                >
                                    Current {frame.frameType === VIDEO ? "Video " : "Image "}
                                </Typography>
                            </ImageFrame>
                        </ImageContainer>
                    )}

                    {mode === FILE && (
                        <>
                            <FadeContainer
                                ref={(el) => {
                                    if (el && !fadeContainerRefs.current.includes(el)) {
                                        fadeContainerRefs.current.push(el);
                                    }
                                }}
                            >
                                <Typography
                                    style={{ textTransform: "capitalize" }}
                                    variant="h3Custom"
                                    color="#FBFCFF"
                                    fontSize="18px"
                                    FontWeight="700"
                                    margin={"30px 0px 15px 2px"}
                                >
                                    Add {frame.frameType === VIDEO ? "Video" : "Image"}
                                </Typography>
                            </FadeContainer>

                            <FadeContainer
                                ref={(el) => {
                                    if (el && !fadeContainerRefs.current.includes(el)) {
                                        fadeContainerRefs.current.push(el);
                                    }
                                }}
                            >
                                <FormInputWrapper file={file} style={{ cursor: "pointer" }}>
                                    <FormInput
                                        accept="image/*"
                                        required
                                        id="fileInput"
                                        type="file"
                                        name="file"
                                        onChange={handleFileChange}
                                        file={file}
                                    />

                                    <SvgHolder file={file}>
                                        {!file ? (
                                            <>
                                                <svg
                                                    width="47"
                                                    height="30"
                                                    viewBox="0 0 47 30"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M37.5323 11.325C36.2134 4.85625 30.3362 0 23.2759 0C17.6703 0 12.8017 3.075 10.3772 7.575C4.53879 8.175 0 12.9563 0 18.75C0 24.9562 5.21767 30 11.6379 30H36.8534C42.2069 30 46.5517 25.8 46.5517 20.625C46.5517 15.675 42.5754 11.6625 37.5323 11.325ZM27.1552 16.875V24.375H19.3966V16.875H13.5776L23.2759 7.5L32.9741 16.875H27.1552Z"
                                                        fill="#BEAEFF"
                                                    />
                                                </svg>

                                                <TextFileUpload>Drag or browse a file to upload.</TextFileUpload>
                                            </>
                                        ) : (
                                            <ImageSelectedContainer>
                                                {/* <TextFileUpload>Selected File</TextFileUpload> */}
                                                <TextFileUploadComplete>{fileName}</TextFileUploadComplete>
                                                <Iconify
                                                    icon={DeleteIcon}
                                                    onClick={clearAllOptions}
                                                    Color={"grey"}
                                                    style={{
                                                        height: "25px",
                                                        margin: "5px",
                                                        cursor: "pointer",
                                                        zIndex: "9999",
                                                    }}
                                                />
                                            </ImageSelectedContainer>
                                        )}
                                    </SvgHolder>
                                </FormInputWrapper>
                            </FadeContainer>
                        </>
                    )}
                    <FadeContainer
                        ref={(el) => {
                            if (el && !fadeContainerRefs.current.includes(el)) {
                                fadeContainerRefs.current.push(el);
                            }
                        }}
                    >
                        <InfoContainer>
                            <SpanRow>
                                <InfoContainerRowText>
                                    <span>File Type: </span>

                                    {(() => {
                                        if (frame.frameType === VIDEO) {
                                            return "MP4 or MOV";
                                        }
                                        return "PNG, JPEG, JPG";
                                    })()}
                                </InfoContainerRowText>
                            </SpanRow>
                            <SpanRow>
                                <InfoContainerRowText>
                                    <span>Recommended size: </span>
                                    {frame.frameType === "thumbnail" ? "490 x 274" : frame.recommendedFileSize} pixels
                                </InfoContainerRowText>
                            </SpanRow>
                            <SpanRow>
                                <InfoContainerRowText>
                                    <span>Maximun file size: </span> 5 MB
                                </InfoContainerRowText>
                            </SpanRow>
                        </InfoContainer>
                    </FadeContainer>

                    <FadeContainer
                        ref={(el) => {
                            if (el && !fadeContainerRefs.current.includes(el)) {
                                fadeContainerRefs.current.push(el);
                            }
                        }}
                    >
                        <RenameHeader>
                            <Typography
                                style={{ textTransform: "capitalize" }}
                                variant="h3Custom"
                                color="#FBFCFF"
                                fontSize="18px"
                                FontWeight="700"
                                margin={"15px 0px 15px 2px"}
                            >
                                Rename
                            </Typography>
                            <RenameInfoContainer>
                                <OptionalText>Optional</OptionalText>
                                <InfoContainerRowText align="right" letterSpacing="1.6px">
                                    <span> {optionalName.length}/50 </span>
                                </InfoContainerRowText>
                            </RenameInfoContainer>
                        </RenameHeader>

                        <RenameInput
                            placeholder="Name this Video"
                            value={optionalName}
                            onChange={(e) => {
                                if (e.target.value.length <= 50) {
                                    setOptionalName(e.target.value);
                                }
                            }}
                        />

                        <InfoContainer>
                            <SpanRow>
                                <InfoContainerRowText>Add a description here.</InfoContainerRowText>
                            </SpanRow>
                            <SpanRow>
                                <InfoContainerRowText>
                                    <span>Character Limit: </span>
                                    50
                                </InfoContainerRowText>
                            </SpanRow>
                        </InfoContainer>
                    </FadeContainer>

                    <Button
                        variant="PurpleButton"
                        loading={isLoading}
                        onClick={() => handleSubmit()}
                        label={"Add Media"}
                        margin={"15px 0 0 0"}
                        style={{ width: "100%" }}
                        disabled={!inputValue && !file && !isLoading}
                    />

                    <Typography
                        style={{ cursor: "pointer" }}
                        variant="pWhite"
                        FontWeight={"700"}
                        letterSpacing={"0px"}
                        fontSize={"16px"}
                        onClick={() => {
                            clearAllOptions();
                            setIsOpen(false);
                        }}
                        margin={"20px 0 0 0"}
                    >
                        Go Back
                    </Typography>
                </>
            )}
        </MediaUploaderContainer>
    );
};

const CropContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin: 25px 0;
`;

const UserConsoleText = styled.h1`
    color: ${setColor.iconColor3};
    font-family: "Barlow Condensed", sans-serif;
    font-size: 36px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 1.6px;
    text-transform: uppercase;
    margin: 1rem 0rem;
`;

const MediaUploaderContainer = styled.div``;

const FormInputWrapper = styled.div`
    border-radius: 5px;
    margin-top: 10px;
    border: ${(props) => (props.file ? `none` : `2.5px dashed ${setColor.greyBackground}`)};
    display: flex;
    position: relative;
    align-items: center;
    justify-content: ${(props) => (props.file ? "flex-start" : "center")};
    z-index: 2;
    background: ${(props) => (props.file ? `${setColor.blackLightGrey15}` : ` rgba(0, 0, 0, 0.65)`)};
    padding: ${(props) => (props.file ? "5px 10px" : "0px")};
`;

const SvgHolder = styled.div`
    display: flex;
    flex-direction: column;
    align-items: ${(props) => (props.file ? "flex-start" : "center")};
    position: absolute;
    width: 100%;
`;

const FormInput = styled.input`
    height: ${(props) => (props.file ? "3vh" : "15vh")};
    opacity: 0;
    width: 100%;
    z-index: 4;
    cursor: pointer;
    align-items: center;
`;

const TextFileUpload = styled.h3`
    font-family: ${setFont.barlow};
    color: ${setColor.iconColor};
    font-weight: 500;
    font-size: 16px;
    margin-top: 15px;
`;

const TextFileUploadComplete = styled.h3`
    leading-trim: both;
    color: ${setColor.blackLightGrey};
    text-edge: cap;
    text-overflow: ellipsis;
    font-family: Barlow;
    font-size: 16px;
    font-style: italic;
    font-weight: 600;
    line-height: normal;
`;

const UrlInput = styled.input`
    margin: 20px 0px;
    width: 100%;
    height: 50px;
    background-color: transparent;
    padding-left: 8px;
    border: 0.2px solid white;
    border-radius: 5px;
    color: white;
    padding-left: 10px;
    box-sizing: border-box;
`;

const SmallSubtitle = styled.p`
    text-align: center;
    color: ${setColor.circularProgress};
    text-align: center;
    font-family: Barlow;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0px;

    & > span {
        color: ${setColor.spanTextcolor2};
        font-weight: 700;
    }
`;

const InfoContainer = styled.div`
    background-color: ${setColor.brightVioletTransparent10};
    margin-top: 15px;
    box-sizing: border-box;
    padding: 10px;
    display: flex;
    padding: 16px 20px;
    flex-direction: column;
    justify-content: center;

    gap: 14px;
    align-self: stretch;
`;

const SpanRow = styled.span`
    font-size: 16px;
    display: flex;
    color: ${setColor.iconColor};
`;

const InfoContainerRowText = styled.p`
    color: ${setColor.blackLightGrey};
    text-align: ${(props) => (props.align ? props.align : "center")};
    font-family: Barlow;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: ${(props) => (props.letterSpacing ? props.letterSpacing : "0px")};

    & > span {
        font-weight: 700;
    }
`;

const RenameInput = styled.input`
    display: flex;
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    background: ${setColor.black40};
    height: 35px;
    border: none;
    border-radius: 5px;
    color: ${setColor.blackLightGrey};
    leading-trim: both;
    text-edge: cap;
    text-overflow: ellipsis;
    font-family: Barlow, sans-serif;
    font-size: 16px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;
    padding-left: 12px;

    &:focus {
        outline: none;
        border: none;
        box-shadow: none;
        color: ${setColor.circularProgress};
        font-weight: medium;
    }

    &::placeholder {
        color: ${setColor.blackLightGrey};
        font-family: Barlow, sans-serif;
        font-size: 16px;
        font-style: italic;
        font-weight: 400;
        line-height: normal;
        opacity: 1;
    }
`;

const RenameHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
`;

const RenameInfoContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
`;

const OptionalText = styled.p`
    color: ${setColor.spanTextcolor2};
    text-align: right;
    leading-trim: both;
    text-edge: cap;
    font-family: Barlow;
    font-size: 14px;
    font-style: italic;
    font-weight: 400;
    line-height: normal;
`;

const ImageSelectedContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 0 0;
    width: 100%;
`;

const ImageContainer = styled.div`
    margin-top: 20px;
    display: flex;
    width: 100%;
    padding: 15px 20px;
    justify-content: center;
    align-items: center;
    gap: 30px;
    background: linear-gradient(
        90deg,
        rgba(157, 117, 245, 0) 0%,
        rgba(157, 117, 245, 0.1) 25.15%,
        rgba(157, 117, 245, 0.1) 75.45%,
        rgba(157, 117, 245, 0) 100.61%
    );
`;

const ImageFrame = styled.div`
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
`;

const PreviewImage = styled.img`
    max-width: 100%;
    max-height: 300px;
    object-fit: contain;
`;

const FadeContainer = styled.div`
    transition: opacity 0.3s ease;
    opacity: 1;

    &.fade {
        opacity: 0.3;
    }
`;

const Text = styled.strong`
    display: flex;
    font-size: 16px;
`;

const TextLight = styled.p`
    padding-left: 5px;
    font-weight: 400;
`;

const ButtonsContainer = styled.div`
    display: flex;
    font-size: 20px;
    font-family: ${setFont.barlow};
    justify-content: space-between;
`;

const CustomButton = styled.div`
    display: flex;
    background-color: ${(props) => (props.isActive ? `${setColor.iconColor3}` : "")};
    background-color: ${(props) => (props.isVideo ? `grey` : "")};
    border: ${(props) => (props.isActive ? `1px solid` : "1px solid #2B2A38")};
    color: ${(props) => (props.isActive ? `${setColor.inputBgColor}` : `${setColor.iconColor}`)};
    font-weight: ${(props) => (props.isActive ? "700" : "400")};
    font-family: ${setFont.barlow};
    width: 285px;
    height: 64px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
        cursor: pointer;
    }
`;

export default MediaUploader;
